import React from 'react';
import { ProductPrice as DesignSystemProductPrice } from '@noths/polaris-design-system-candidate-components';

import type { ProductState } from 'src/redux/product/types';
import * as styles from './ProductPrice.styles';

export interface ProductPriceProps {
  preSalePrices?: ProductState['preSalePrices'];
  price: ProductState['price'];
  priceIsMinimum?: boolean;
  /**
   * Whether the site is presenting discounts as a sale rather than as an offer.
   * Set site-wide in Contentstack, so the same discount reads the same way here as
   * it does on the card the customer arrived from.
   */
  promoteAsSale?: boolean;
  /** The saving the service quotes, rather than one derived from the two prices. */
  salePercentage?: number;
}

/**
 * The PDP's price block, on the same component as the product card, so a discount
 * is presented identically on both surfaces and follows the one global switch.
 *
 * The saving comes from the service rather than being derived from the prices, as
 * the card derives it. The PDP has always quoted the service's figure and the two
 * can round differently, so changing which one the page shows is a separate
 * decision from changing how it is presented.
 */
export const ProductPrice = ({
  preSalePrices,
  price,
  priceIsMinimum = false,
  promoteAsSale = false,
  salePercentage = 0,
}: ProductPriceProps) => {
  const { currency } = price;
  const preSalePrice = preSalePrices?.find((preSalePrice) => preSalePrice.currency === currency);

  /* Only a genuinely higher was-price is a discount, matching how the card reads
     the same data, so an equal or lower one renders as an ordinary price on both
     surfaces rather than as a saving of nothing. */
  const originalPrice =
    preSalePrice && preSalePrice.amount > price.amount ? preSalePrice.amount : undefined;

  /* The service can quote a saving without a was-price in the currency on display,
     and the page has always shown the percentage on its own in that case. */
  const isDiscounted = originalPrice !== undefined || salePercentage > 0;

  return (
    <div css={styles.root}>
      <DesignSystemProductPrice
        currencyCode={currency}
        discount={isDiscounted ? { originalPrice, percentage: salePercentage } : undefined}
        price={price.amount}
        priceIsMinimum={priceIsMinimum}
        promoteAsSale={promoteAsSale}
        variant="lg"
      />
    </div>
  );
};
